import React, {useEffect, useState} from 'react'
import {
  IdDescriptionDTOListApiData,
  ProductPurchaseReport,
  ProductPurchaseReportRequest,
  ProductPurchaseReportResponseApiData,
  ProductReport,
} from '../../../../api/data-contracts'
import {
  Api_Admin_GetProductPurchaseReport,
  Api_Admin_GetProducts,
} from '../../../../apiClient/apiClient'
import {getAdminState, setReport} from '../../../../redux/adminSlice'
import {getRequestAsync, postRequestAsync, setIsLoading} from '../../../../redux/commonSlice'
import {useDispatch, useSelector} from 'react-redux'
import BootstrapTable, {ColumnDescription} from 'react-bootstrap-table-next'
import ExportCSV from '../../../../components/admin/common/ExportCSV'
import {ReportEnum} from '../../../../types/ReportEnum'
import FormikForm from '../../../../components/form/FormikForm'
import Select from 'react-select'
import paginationFactory, {
  PaginationProvider,
  SizePerPageDropdownStandalone,
  PaginationTotalStandalone,
  PaginationListStandalone,
} from 'react-bootstrap-table2-paginator'
import {DropdownOption} from '../../../../components/form/dropdown/CustomDropdown'

interface DateKey {
  year: number
  month: number
}

interface TableRow {
  dateKey: DateKey
  dateString: string
  [key: string]: any // Dynamic product columns
}

function ProductPurchaseReportComponent() {
  const adminState = useSelector(getAdminState)
  const dispatch = useDispatch()
  const [products, setProducts] = useState<DropdownOption[]>([])

  // Create a map of productId -> productName
  const productMap = React.useMemo(() => {
    const map = new Map<number, string>()
    products.forEach((product) => {
      map.set(parseInt(product.value), product.label)
    })
    return map
  }, [products])

  // Calculate all unique dates from all ProductPurchaseReport arrays
  const dateList: DateKey[] = React.useMemo(() => {
    if (!adminState?.reports.productPurchaseReport) return []

    const dateSet = new Set<string>()
    adminState.reports.productPurchaseReport.forEach((productReport: ProductReport) => {
      productReport.reportList?.forEach((report: ProductPurchaseReport) => {
        if (report.reportYear && report.reportMonth) {
          dateSet.add(`${report.reportYear}-${report.reportMonth}`)
        }
      })
    })

    return Array.from(dateSet)
      .map((dateStr) => {
        const [year, month] = dateStr.split('-').map(Number)
        return {year, month}
      })
      .sort((a, b) => {
        if (a.year !== b.year) return b.year - a.year
        return b.month - a.month
      })
  }, [adminState?.reports.productPurchaseReport])

  // Build table columns for BootstrapTable (headers will be hidden)
  const columns: ColumnDescription[] = React.useMemo(() => {
    const cols: ColumnDescription[] = [
      {
        dataField: 'dateString',
        text: '',
        sort: false,
        headerStyle: {width: '15%'},
        classes: 'sticky-date-column',
        formatter: (cell: any, row: TableRow) => (
          <div className='text-dark fw-bold d-block fs-6 text-start'>{cell}</div>
        ),
      },
    ]

    // Add columns for each product
    adminState?.reports.productPurchaseReport?.forEach(
      (productReport: ProductReport, index: number) => {
        const productId = productReport.productId ?? 0

        // Purchase Count column
        cols.push({
          dataField: `product_${productId}_purchaseCount`,
          text: '',
          sort: false,
          formatter: (cell: any) => (
            <div className='text-dark fw-bold d-block fs-6 text-end'>
              {cell != null ? cell.toFixed(0) : '-'}
            </div>
          ),
        })

        // Total Amount column
        cols.push({
          dataField: `product_${productId}_totalAmount`,
          text: '',
          sort: false,
          formatter: (cell: any) => (
            <div className='text-dark fw-bold d-block fs-6 text-end'>
              {cell != null ? `${cell.toFixed(0)} ₺` : '-'}
            </div>
          ),
        })

        // Defterdar Amount column
        cols.push({
          dataField: `product_${productId}_defterdarAmount`,
          text: '',
          sort: false,
          formatter: (cell: any) => (
            <div className='text-dark fw-bold d-block fs-6 text-end'>
              {cell != null ? `${cell.toFixed(0)} ₺` : '-'}
            </div>
          ),
        })
      }
    )

    return cols
  }, [adminState?.reports.productPurchaseReport])

  // Build custom header row for product names
  const customHeaderRow = React.useMemo(() => {
    if (
      !adminState?.reports.productPurchaseReport ||
      adminState.reports.productPurchaseReport.length === 0
    ) {
      return null
    }

    return (
      <thead>
        <tr>
          <th
            rowSpan={2}
            className='text-start align-middle sticky-date-column-header'
            style={{width: '15%', borderBottom: '1px solid #dee2e6'}}
          >
            <div className='text-dark fw-bold d-block fs-6 text-start'>Tarih</div>
          </th>
          {adminState.reports.productPurchaseReport.map(
            (productReport: ProductReport, index: number) => {
              const productId = productReport.productId ?? 0
              const productName = productMap.get(productId) || `Ürün ${productId}`
              return (
                <th
                  key={`header_${productId}`}
                  colSpan={3}
                  className='text-center align-middle'
                  style={{
                    borderBottom: '1px solid #dee2e6',
                  }}
                >
                  <div className='text-dark fw-bold d-block fs-6 text-center'>{productName}</div>
                </th>
              )
            }
          )}
        </tr>
        <tr>
          {adminState.reports.productPurchaseReport.map(
            (productReport: ProductReport, index: number) => {
              const productId = productReport.productId ?? 0
              return (
                <React.Fragment key={`subheader_${productId}`}>
                  <th
                    className='text-end align-middle'
                    style={{
                      borderBottom: '1px solid #dee2e6',
                    }}
                  >
                    <div className='text-dark fw-bold d-block fs-6 text-end'>Satış Adedi</div>
                  </th>
                  <th className='text-end align-middle' style={{borderBottom: '1px solid #dee2e6'}}>
                    <div className='text-dark fw-bold d-block fs-6 text-end'>Toplam Tutar</div>
                  </th>
                  <th className='text-end align-middle' style={{borderBottom: '1px solid #dee2e6'}}>
                    <div className='text-dark fw-bold d-block fs-6 text-end'>Defterdar Tutarı</div>
                  </th>
                </React.Fragment>
              )
            }
          )}
        </tr>
      </thead>
    )
  }, [adminState?.reports.productPurchaseReport, productMap])

  // Build table data rows
  const tableData: TableRow[] = React.useMemo(() => {
    if (!adminState?.reports.productPurchaseReport || dateList.length === 0) return []

    // Create a map for quick lookup: dateKey -> productId -> report data
    const reportMap = new Map<string, Map<number, ProductPurchaseReport>>()

    adminState.reports.productPurchaseReport.forEach((productReport: ProductReport) => {
      const productId = productReport.productId ?? 0
      productReport.reportList?.forEach((report: ProductPurchaseReport) => {
        if (report.reportYear && report.reportMonth) {
          const dateKey = `${report.reportYear}-${report.reportMonth}`
          if (!reportMap.has(dateKey)) {
            reportMap.set(dateKey, new Map())
          }
          reportMap.get(dateKey)!.set(productId, report)
        }
      })
    })

    // Build rows
    return dateList.map((dateKey) => {
      const date = new Date(dateKey.year, dateKey.month - 1)
      const monthName = date.toLocaleString('tr-TR', {month: 'long'})
      const row: TableRow = {
        dateKey,
        dateString: `${monthName} ${dateKey.year}`,
      }

      // Add data for each product
      adminState.reports.productPurchaseReport?.forEach((productReport: ProductReport) => {
        const productId = productReport.productId ?? 0
        const dateStr = `${dateKey.year}-${dateKey.month}`
        const report = reportMap.get(dateStr)?.get(productId)

        row[`product_${productId}_purchaseCount`] = report?.purchaseCount ?? null
        row[`product_${productId}_totalAmount`] = report?.totalAmount ?? null
        row[`product_${productId}_defterdarAmount`] = report?.defterdarAmount ?? null
      })

      return row
    })
  }, [adminState?.reports.productPurchaseReport, dateList])

  function getExcelData() {
    const excelData: any[] = []
    tableData.forEach((row) => {
      const excelRow: any = {
        Tarih: row.dateString,
      }

      adminState?.reports.productPurchaseReport?.forEach((productReport: ProductReport) => {
        const productId = productReport.productId ?? 0
        const productName = productMap.get(productId) || `Ürün ${productId}`

        excelRow[`${productName} - Satış Adedi`] = row[`product_${productId}_purchaseCount`] ?? '-'
        excelRow[`${productName} - Toplam Tutar`] = row[`product_${productId}_totalAmount`] ?? '-'
        excelRow[`${productName} - Defterdar Tutarı`] =
          row[`product_${productId}_defterdarAmount`] ?? '-'
      })

      excelData.push(excelRow)
    })

    return excelData
  }

  const initFilterValues: ProductPurchaseReportRequest = {
    productIds: null,
  }

  const loadReport = (request: ProductPurchaseReportRequest) => {
    dispatch(setIsLoading(true))
    dispatch<any>(
      postRequestAsync({
        postRequestFunction: Api_Admin_GetProductPurchaseReport,
        body: request,
      })
    )
      .unwrap()
      .then(async function (response: ProductPurchaseReportResponseApiData) {
        dispatch(
          setReport({
            reportEnum: ReportEnum.ProductPurchaseReport,
            productPurchaseReport: response.data?.productPurchaseReports ?? [],
          })
        )
        dispatch(setIsLoading(false))
      })
      .catch(() => {
        dispatch(setIsLoading(false))
      })
  }

  useEffect(() => {
    // Load products for dropdown
    dispatch<any>(
      getRequestAsync({
        getRequestFunction: Api_Admin_GetProducts,
      })
    )
      .unwrap()
      .then(async function (response: IdDescriptionDTOListApiData) {
        const productOptions: DropdownOption[] =
          response.data
            ?.map((product: any) => {
              const type = product.productTypeName ?? product.type ?? ''
              const title = product.title ?? product.description ?? ''
              const label = type && title ? `${type} - ${title}` : title || type || ''
              return {
                label: label,
                value: (product.id ?? 0).toString(),
              }
            })
            .sort((a, b) => a.label.localeCompare(b.label)) ?? []
        setProducts(productOptions)
      })

    // Load initial report
    loadReport(initFilterValues)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div>
      <style>{`
        .sticky-date-column {
          position: sticky;
          left: 0;
          background-color: white;
          z-index: 10;
        }
        .sticky-date-column-header {
          position: sticky;
          left: 0;
          background-color: white;
          z-index: 20;
        }
      `}</style>
      <div className='row mb-7'>
        <FormikForm initialValues={initFilterValues} onSubmit={loadReport}>
          {(props) => {
            return (
              <>
                <div className='row'>
                  <div className='d-flex flex-column mb-3 fv-row col-xl-8'>
                    <label className='d-flex align-items-center fs-6 fw-bold form-label mb-2'>
                      <span className=''>Ürünler</span>
                    </label>
                    <Select
                      isMulti
                      options={products}
                      closeMenuOnSelect={false}
                      name='productIds'
                      onChange={(selectedOptions) => {
                        const productIds = (selectedOptions as DropdownOption[]).map((opt) =>
                          parseInt(opt.value)
                        )
                        props.setFieldValue('productIds', productIds.length > 0 ? productIds : null)
                      }}
                      classNames={{
                        control: (state) => 'form-select form-select-solid fs-6',
                      }}
                      styles={{
                        control: (baseStyles, state) => ({
                          ...baseStyles,
                          paddingTop: '0',
                          paddingBottom: '0',
                        }),
                        dropdownIndicator: (baseStyles, state) => ({
                          ...baseStyles,
                          display: 'none',
                        }),
                      }}
                    />
                  </div>

                  <div className='d-flex mb-3 fv-row col-xl-2 align-items-end'>
                    <button
                      type='button'
                      className='btn btn-lg btn-primary me-3 col-12'
                      onClick={() => {
                        props.resetForm({values: initFilterValues})
                      }}
                    >
                      <span className='indicator-label'>Temizle</span>
                    </button>
                  </div>

                  <div className='d-flex mb-3 fv-row col-xl-2 align-items-end'>
                    <button type='submit' className='btn btn-lg btn-primary me-3 col-12'>
                      <span className='indicator-label'>Filtrele</span>
                    </button>
                  </div>
                </div>
              </>
            )
          }}
        </FormikForm>
      </div>

      <div className='table-responsive'>
        <div>
          {tableData.length > 0 && (
            <PaginationProvider
              pagination={paginationFactory({
                custom: true,
                totalSize: tableData.length,
                sizePerPage: 50,
                paginationSize: 50,
                paginationTotalRenderer: (from, to, size) => (
                  <span className='react-bootstrap-table-pagination-total ms-3'>
                    Toplam {size} kayıttan {from} - {to} arası gösteriliyor{' '}
                    <ExportCSV csvData={getExcelData()} fileName={'Ürün Satışı Raporu'} />
                  </span>
                ),
              })}
            >
              {({paginationProps, paginationTableProps}) => (
                <>
                  <SizePerPageDropdownStandalone {...paginationProps} className='mx-5' />
                  <PaginationTotalStandalone {...paginationProps} />
                  <PaginationListStandalone {...paginationProps} />

                  <div>
                    {customHeaderRow && (
                      <div className='table-responsive'>
                        <table
                          className='table mb-0'
                          style={{width: '100%', borderCollapse: 'collapse'}}
                        >
                          {customHeaderRow}
                        </table>
                      </div>
                    )}
                    <div
                      className='table-responsive'
                      style={{marginTop: customHeaderRow ? '-1px' : 0}}
                    >
                      <BootstrapTable
                        {...paginationTableProps}
                        classes='table mb-0'
                        headerClasses='d-none'
                        keyField='dateString'
                        data={tableData}
                        columns={columns}
                        wrapperClasses=''
                      />
                    </div>
                  </div>
                </>
              )}
            </PaginationProvider>
          )}
        </div>
      </div>
    </div>
  )
}

export default ProductPurchaseReportComponent
